import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { token } from '../auth/session';
import { loadAddons } from '../games/host/addons';

type Version = {
  id: string;
  kind: string;
  version: string;
  toolkit: number;
  sha256: string;
  audience: 'NONE' | 'DEVELOPERS' | 'EVERYONE';
  scriptBytes: number;
  styleBytes: number;
  uploadedAt: string;
};

const AUDIENCE_LABEL: Record<Version['audience'], string> = {
  NONE: 'Nobody',
  DEVELOPERS: 'Developers only',
  EVERYONE: 'Everyone',
};

const kb = (n: number) => `${Math.max(1, Math.round(n / 1024))} KB`;

export default function GameTypesAdminPage() {
  const [admin, setAdmin] = useState<boolean | null>(null);
  const [versions, setVersions] = useState<Version[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState('');
  const [version, setVersion] = useState('');
  const [script, setScript] = useState<File | null>(null);
  const [style, setStyle] = useState<File | null>(null);
  const [formKey, setFormKey] = useState(0);

  const load = useCallback(async () => {
    try {
      const me = await api<{ admin: boolean }>('/concepts/approvals/me', { token: true });
      setAdmin(me.admin);
      if (me.admin) setVersions(await api<Version[]>('/admin/addons', { token: true }));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the game types.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  async function upload(e: FormEvent) {
    e.preventDefault();
    if (!script) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const form = new FormData();
      form.append('kind', kind.trim());
      form.append('version', version.trim());
      form.append('toolkit', '1');
      form.append('script', script, script.name);
      if (style) form.append('style', style, style.name);
      const t = await token();
      const res = await fetch('/api/admin/addons', {
        method: 'POST',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
        body: form,
        credentials: 'omit',
      });
      if (!res.ok) {
        let message = `The upload was refused (${res.status}).`;
        try { message = (await res.json()).message ?? message; } catch { message = `The upload was refused (${res.status}).`; }
        throw new Error(message);
      }
      const saved = (await res.json()) as Version;
      setNotice(`Uploaded ${saved.kind} ${saved.version}. Nobody sees it until you choose who does.`);
      setKind('');
      setVersion('');
      setScript(null);
      setStyle(null);
      setFormKey((k) => k + 1);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The upload did not work.');
    } finally {
      setBusy(false);
    }
  }

  async function setAudience(v: Version, audience: Version['audience']) {
    setError(null);
    setNotice(null);
    try {
      await api(`/admin/addons/${v.id}/audience`, { method: 'PUT', token: true, body: { audience } });
      await load();
      await loadAddons(true);
      setNotice(`${v.kind} ${v.version} is now shown to: ${AUDIENCE_LABEL[audience].toLowerCase()}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not change.');
    }
  }

  if (admin === false) return <div className="card"><p>This page is for EasyLearning administrators.</p></div>;
  if (admin === null) return <p className="muted">Loading…</p>;

  return (
    <div>
      <h1>Game types</h1>
      <p className="field-hint">
        Upload a game type built with the game toolkit, then choose who sees it. Nothing restarts and nobody
        using the app is interrupted. People already in the app pick up a change within a minute, the next time
        they open a game. Developers see a version set to developers only instead of the one set to everyone, so
        a new version can be tried before it goes to everyone. To go back, choose an earlier version.
      </p>
      {error && <div className="error" role="alert">{error}</div>}
      {notice && <p className="muted" role="status">{notice}</p>}

      <form className="card" onSubmit={upload} key={formKey}>
        <h2>Upload a version</h2>
        <div className="field">
          <label htmlFor="gt-kind">Kind</label>
          <input id="gt-kind" value={kind} onChange={(e) => setKind(e.target.value)} required placeholder="traceIt" />
          <span className="field-hint">The name programs use for this game type. A new name adds a new type; an existing name replaces that type for the people you choose.</span>
        </div>
        <div className="field">
          <label htmlFor="gt-version">Version</label>
          <input id="gt-version" value={version} onChange={(e) => setVersion(e.target.value)} required placeholder="1.0.0" />
        </div>
        <div className="field">
          <label htmlFor="gt-script">Game file (.js)</label>
          <input id="gt-script" type="file" accept=".js,text/javascript" onChange={(e) => setScript(e.target.files?.[0] ?? null)} required />
        </div>
        <div className="field">
          <label htmlFor="gt-style">Style sheet (.css, if it has one)</label>
          <input id="gt-style" type="file" accept=".css,text/css" onChange={(e) => setStyle(e.target.files?.[0] ?? null)} />
        </div>
        <div className="toolbar">
          <button className="btn" type="submit" disabled={busy || !script}>{busy ? 'Uploading…' : 'Upload'}</button>
        </div>
      </form>

      <div className="card">
        <h2>Versions</h2>
        {versions && versions.length === 0 && <p className="muted">No game types have been uploaded yet.</p>}
        {versions && versions.length > 0 && (
          <div className="table-wrap">
            <table className="library-table">
              <thead>
                <tr><th>Kind</th><th>Version</th><th>Uploaded</th><th>Size</th><th>Shown to</th></tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr key={v.id}>
                    <td>{v.kind}</td>
                    <td>{v.version}</td>
                    <td>{new Date(v.uploadedAt).toLocaleString()}</td>
                    <td>{kb(v.scriptBytes + v.styleBytes)}</td>
                    <td>
                      <select
                        aria-label={`Who sees ${v.kind} ${v.version}`}
                        value={v.audience}
                        onChange={(e) => void setAudience(v, e.target.value as Version['audience'])}
                      >
                        {(Object.keys(AUDIENCE_LABEL) as Version['audience'][]).map((a) => <option key={a} value={a}>{AUDIENCE_LABEL[a]}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
